import { ChangeDetectionStrategy, Component, computed, effect, inject, Input, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { ISbStoryData } from '@storyblok/js';
import { combineLatest, filter, map, Observable, of, startWith, switchMap } from 'rxjs';
import { getFullPath } from '../../../lib/get-full-path';
import { PICFLOW_STORYBLOK } from '../../../picflow-storyblok';
import { StoryblokHelperService } from '../../../services/storyblok-helper.service';
import { BlockStyle, BlockWrapperComponent } from '../block-wrapper/block-wrapper.component';
import { BlogPostLinkListItemComponent } from '../blog-post-link-list-item/blog-post-link-list-item.component';

@Component({
  selector: 'app-blog-post-link-list',
  templateUrl: './blog-post-link-list.component.html',
  styleUrls: ['./blog-post-link-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BlockWrapperComponent, RouterLink, BlogPostLinkListItemComponent],
})
export class BlogPostLinkListComponent {
  private storyblokService = inject(PICFLOW_STORYBLOK);
  private storyblokHelperService = inject(StoryblokHelperService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  @Input() _uid: string; // just here to prevent errors
  @Input() component: string; // just here to prevent errors
  @Input() _editable: string;
  @Input() style: BlockStyle[];
  @Input() relationshipArrayFields: string;

  pageSize = 15;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  readonly _posts = input<string[]>(undefined, { alias: 'posts' });
  readonly hardcodedPosts = input<(ISbStoryData & { _editable: string })[]>(undefined);
  readonly topic = input<string>(undefined);

  private _posts$ = toObservable(this._posts).pipe(
    map(posts => {
      if (!posts) return { stories: [], total: 0 };
      const relationships = this.storyblokHelperService.getGlobalData('relationships');
      return {
        stories: posts.map(post => relationships.find(page => page.uuid === post)).filter(Boolean) as (ISbStoryData & {
          _editable: string;
        })[],
        total: posts.length,
      };
    })
  );

  page$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    startWith(undefined),
    map(() => {
      const path = getFullPath(this.route.snapshot, false);
      const last = path.pop();
      if (!last || !last.startsWith('page-')) {
        return 1; // Default to page 1 if no page parameter is found
      }
      const page = parseInt(last.split('page-')[1], 10);
      return page;
    })
  );

  posts = toSignal(
    combineLatest([toObservable(this.topic), this._posts$, toObservable(this.hardcodedPosts)]).pipe(
      switchMap(([topic, posts, hardcodedPosts]) => {
        if (hardcodedPosts?.length) {
          return of({
            stories: hardcodedPosts,
            total: hardcodedPosts.length,
          });
        }
        if (posts.total) return this._posts$;

        return this.page$.pipe(
          switchMap(page => {
            return this.storyblokService.getStories({
              per_page: this.pageSize,
              page,
              content_type: 'Article',
              sort_by: 'first_published_at:desc',
              filter_query: [
                topic
                  ? {
                      key: 'topic',
                      value: topic,
                    }
                  : undefined,
              ].filter(Boolean),
            }) as Observable<{ stories: (ISbStoryData & { _editable: string })[]; total: number }>; // Assuming this returns an observable of stories
          })
        );
      })
    )
  );

  activePage = toSignal(this.page$);

  pages = computed(() => {
    const hardCodedPosts = this._posts()?.length;
    const total = this.posts()?.total || 0;
    const activePage = this.activePage();

    if (hardCodedPosts) return [];

    const path = getFullPath(this.route.snapshot);
    return new Array(Math.ceil(total / this.pageSize)).fill(0).map((_, i) => {
      const page = i + 1;
      return {
        number: page,
        active: page === activePage,
        link: page === 1 ? ['/', ...path] : ['/', ...path, `page-${page}`],
      };
    });
  });

  constructor() {
    effect(() => {
      const page = this.activePage();
      const pages = this.pages();
      if (!page || !pages?.length) return;
      if (page > pages.length) {
        this.router.navigate(['/', ...getFullPath(this.route.snapshot), `page-${pages.length}`], {
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    });
  }
}
